import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Close as CloseIcon } from '@mui/icons-material';
import FileBrowser from './FileBrowser';
import DocumentsView from './DocumentsView';
import ReportsView from './ReportsView';
import { SharedView } from './shared-view';
import { BinUploadMatchPrompt, uploadedBins, type UploadedBin } from './flight-links';
import { useAuthV2 } from '../hooks/useAuthV2';
import { isAdmin as checkIsAdmin } from '../config/roles';
import { BRAND_COLORS } from '../styles/theme';
import { FileFilters } from './Dashboard/types/dashboard.types';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { useTranslation } from '../i18n';
import {
  useFolderChildrenQuery,
  useUploadFileMutation,
  useAnalyzeFileMutation,
  useGetDownloadUrlMutation,
  useDeleteObjectMutation,
  useMoveItemMutation,
  useRenameObjectMutation,
  useCreateFolderMutation,
  type FolderListParams,
  type UploadFileMetadata,
} from './fileManagement.queries';
import { moveOutcome, summarizeMoveResults, type MoveOutcome } from './file-browser/moveResults';
import { ROOT_FOLDER, crumbRefs, isCurrentNode } from './file-browser/navigationModel';
import { FILES_ROOT_PATH, folderUrl } from './file-browser/folderUrl';
import { folderLoadFailure } from './file-browser/folderLoadFailure';
import FolderNotFound from './file-browser/FolderNotFound';
import { useDashboardStore } from '../stores/dashboardStore';
import { replaceUrlState } from '../stores/dashboardUrlSync';
import { isForeignFolder, listItems, pageEntryCount } from './file-browser/childrenModel';
import { parentIdFor } from './file-browser/writeTargets';
import {
  formatFileWarnings,
  summarizeUploadResults,
  type UploadOutcome,
} from './file-uploader/uploadResults';
import type { FileItem, FolderRef } from './file-browser';

const FileManagementContainer = styled(Paper)({
  backgroundColor: BRAND_COLORS.white.main,
  borderRadius: '12px',
  padding: '24px',
  minHeight: '600px',
});

const CloseButton = styled(Button)({
  position: 'absolute',
  right: '8px',
  top: '8px',
  minWidth: 'auto',
  padding: '8px',
  color: '#666',
});

interface FileManagementProps {
  onClose?: () => void;
  fileFilters?: FileFilters;
  serialNumbers?: string[];
  onFileFilterChange?: (filters: Partial<FileFilters>) => void;
  onResetFileFilters?: () => void;
}

const FileManagement: React.FC<FileManagementProps> = ({
  onClose,
  fileFilters,
  serialNumbers,
  onFileFilterChange,
  onResetFileFilters,
}) => {
  const { showError, showSuccess, showWarning, showInfo } = useErrorHandler();
  const { t } = useTranslation();

  // Authority to GRANT and REVOKE, which is `require_org_admin` on the API.
  // Not the authority to SEE the Shared view — that is plain authenticated
  // read, gated in `FileBrowser` as `canViewShares`. The two are separate
  // capabilities and are computed separately.
  const { profile } = useAuthV2();
  const canAdministerShares = checkIsAdmin(profile?.organization?.roles);
  // Where we are, as a NODE and nothing else (STD-029 R-10). `id: null` means
  // the caller's own organization root — the one node no listing names, which
  // the server resolves from the JWT. B-4 removed the path half; the listing is
  // `GET /api/file-objects/{id}/children` and every write takes a row id.
  //
  // **It lives in the store since B-5, and the URL is its source of truth.** Two
  // things follow that a user can see: a folder is addressable (`/files/folder/
  // <id>`, reloadable, back-able, pasteable — for a shared folder too), and it
  // survives leaving the tab, which component state could not because it died
  // with the unmounted tab.
  const filesFolderId = useDashboardStore((state) => state.filesFolderId);
  const filesFolderInvalid = useDashboardStore((state) => state.filesFolderInvalid);
  const setFilesFolder = useDashboardStore((state) => state.setFilesFolder);
  // Only so the canonicalizer can rebuild the whole url rather than half of it.
  // The Files tab is tier-1, so `buildDashboardUrl` drops it — this is here for
  // the day the tab stops being tier-1, not because it changes today's output.
  const selectedSerial = useDashboardStore((state) => state.selectedSerial);
  const currentFolder = useMemo<FolderRef>(() => ({ id: filesFolderId }), [filesFolderId]);
  const [uploadLoading, setUploadLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [analysisResult, setAnalysisResult] = useState<
    | {
        success: boolean;
        metadata: Record<string, unknown>;
        rows_processed: number;
        columns_processed: number;
      }
    | undefined
  >(undefined);
  const [showDocumentsView, setShowDocumentsView] = useState<boolean>(false);
  const [showReportsView, setShowReportsView] = useState<boolean>(false);
  // "Shared with me" (#1242) — a peer view of Documents/Reports, deliberately
  // NOT a pseudo-folder inside the browser. It is a listing over the two grant
  // tables; rendering it as a directory would put the trust boundary back in an
  // S3 path, which is what #1182 Stages 1-2 removed.
  //
  // ONE destination, not two. There used to be a second state for a second
  // screen ("Shared files"), which partitioned by which grant table was written
  // — a split a user has no way to see. Flights and Files are now a toggle
  // inside the one view.
  const [showSharedView, setShowSharedView] = useState<boolean>(false);
  const [duplicateFiles, setDuplicateFiles] = useState<{ fileName: string; message: string }[]>([]);
  // The .bin logs a batch just stored, to ask "is this the same flight?" about (#1694).
  const [binsToAsk, setBinsToAsk] = useState<UploadedBin[]>([]);
  const clearBinsToAsk = useCallback(() => setBinsToAsk([]), []);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);

  // The query key, and it is the NODE id — §4's "navigation cache keyed by node
  // id". Sorting joined it in B-4 because the server sorts now: the page is
  // bounded and folder rows order on aggregates only the server can compute, so
  // sorting a truncated page on the client would be a lie about which rows you
  // were given.
  const queryParams: FolderListParams = useMemo(
    () => ({
      nodeId: currentFolder.id,
      selectedSerial: fileFilters?.selectedSerial,
      startDate: fileFilters?.startDate ?? undefined,
      endDate: fileFilters?.endDate ?? undefined,
      includeUntagged: fileFilters?.includeUntagged,
      sortBy: fileFilters?.sortBy,
      sortOrder: fileFilters?.sortOrder,
    }),
    [
      currentFolder.id,
      fileFilters?.selectedSerial,
      fileFilters?.startDate,
      fileFilters?.endDate,
      fileFilters?.includeUntagged,
      fileFilters?.sortBy,
      fileFilters?.sortOrder,
    ]
  );

  // --- Read ---
  // Withheld when the url named something that cannot be an id: `nodeId` is
  // `null` in that case, so an enabled query would fetch the caller's own root
  // in the background while the dead-link panel is what renders.
  const childrenQuery = useFolderChildrenQuery(queryParams, { enabled: !filesFolderInvalid });
  const listing = childrenQuery.data;
  // Registry children first, then any R-9 unregistered flights. The server
  // already filtered, aggregated and sorted; this is a rename of fields.
  const files = useMemo(() => listItems(listing), [listing]);
  const crumbs = useMemo(() => crumbRefs(listing, currentFolder), [listing, currentFolder]);
  // The open folder's OWNER vs ours. Two things turn on it, both stated where
  // they are used: the breadcrumb renders as a share, and the upload zone is
  // withheld.
  const insideShare = isForeignFolder(listing, profile?.organization?.id);
  const loading = childrenQuery.isLoading;

  // Why the listing failed, as three different sentences (B-5). A dead deep
  // link and a registry outage are not the same event and must not read the
  // same: `unavailable` keeps the inline <Alert> above the browser, the other
  // two replace the browser with the dead-link panel.
  const failure = childrenQuery.isError ? folderLoadFailure(childrenQuery.error) : undefined;
  // A url segment that cannot be an id never reaches the API at all — FastAPI
  // would answer 422 for it, which the classifier would then report as "that
  // link points at a file". Wrong sentence for a mistyped link.
  const deadLink: 'not-found' | 'not-a-folder' | undefined = filesFolderInvalid
    ? 'not-found'
    : failure === 'unavailable'
      ? undefined
      : failure;

  // Reflect file list query errors inline (same as before, minus the two the
  // panel now owns)
  useEffect(() => {
    if (childrenQuery.isError && failure === 'unavailable') {
      setError(t('fileManagement.failedToLoadFiles'));
    } else if (childrenQuery.isSuccess || childrenQuery.isError) {
      setError('');
    }
  }, [childrenQuery.isError, childrenQuery.isSuccess, failure, t]);

  // A folder change closes the peer views. Without this, pressing Back while
  // Shared/Documents/Reports is open changes the url and the folder underneath
  // it and appears to do nothing at all.
  useEffect(() => {
    setShowSharedView(false);
    setShowDocumentsView(false);
    setShowReportsView(false);
  }, [filesFolderId]);

  // The canonicalizer. Once the listing resolves it, and only then, do we know
  // the folder's NAME (for the slug) and whether it is the caller's own root
  // (which has exactly one url, `/files`). Both are `replaceState`: a beautified
  // url is the same place, not a new one, so it must not cost a Back press.
  //
  // Guarded twice — on the id, so a stale listing cannot stamp the url for a
  // folder we have already left, and on inequality, so it cannot loop.
  useEffect(() => {
    const node = listing?.node;
    if (!node || node.object_id !== filesFolderId) return;
    const ownRoot = node.parent_id === null && node.organization_id === profile?.organization?.id;
    // The NODE keeps its real id; only the URL is normalized. `{id: null}`
    // aliases the root and that alias is what the clipboard-origin bug needed.
    const canonical = ownRoot ? FILES_ROOT_PATH : folderUrl(node.object_id, node.name);
    if (window.location.pathname === canonical) return;
    replaceUrlState('files', selectedSerial, ownRoot ? null : node.object_id, node.name);
  }, [listing, filesFolderId, profile?.organization?.id, selectedSerial]);

  // --- Mutations (invalidation is handled in the hook; each hook receives queryParams) ---
  const uploadFileMutation = useUploadFileMutation();
  const analyzeFileMutation = useAnalyzeFileMutation();
  const getDownloadUrlMutation = useGetDownloadUrlMutation();
  // No params: every write invalidates the whole `files` domain. A move changes
  // TWO folders — the source and the destination — and the client does not know
  // the destination's ancestors, so a per-folder invalidation would leave one of
  // them stale. The recursive projection hid this by keeping the whole tree
  // under one key.
  const deleteObjectMutation = useDeleteObjectMutation();
  const moveItemMutation = useMoveItemMutation();
  const renameObjectMutation = useRenameObjectMutation();
  const createFolderMutation = useCreateFolderMutation();

  const handleUpload = async (
    files: File[],
    metadata: UploadFileMetadata
  ): Promise<UploadOutcome> => {
    setUploadLoading(true);
    setError('');

    try {
      // Inside a folder another organization shared the upload is id-addressed
      // (#1351); the mutation takes that decision, per file, from the target.
      const target = { insideShare, currentFolder };
      const results = await Promise.allSettled(
        files.map((file) => uploadFileMutation.mutateAsync({ file, metadata, target }))
      );

      const summary = summarizeUploadResults(
        results,
        t('fileManagement.unknownFile'),
        files.map((file) => file.name)
      );

      if (summary.uploadedCount > 0) {
        showSuccess(t('fileManagement.uploadSuccess', { count: summary.uploadedCount }));
        setAnalysisResult(undefined);
      }

      // A qualified success (#1253). Rendered after the success toast and never
      // as an error: these files ARE stored, so they clear from the selection
      // like any other success and only genuine failures stay selected. Split by
      // severity because "this log had no hours in it" and "sharing will not work
      // on this file until you re-upload it" ask different things of the reader.
      const actionRequired = summary.warnings.filter((warning) => warning.actionRequired);
      const informational = summary.warnings.filter((warning) => !warning.actionRequired);
      if (actionRequired.length > 0) {
        showWarning(formatFileWarnings(actionRequired));
      }
      if (informational.length > 0) {
        showInfo(formatFileWarnings(informational));
      }

      // Fewer clicks (#1694): each stored .bin is matched against the flights the
      // uploader can see on its day, and a likely match is asked about right
      // away. Nothing is linked without a Yes.
      const bins = uploadedBins(
        results,
        files.map((file) => file.name)
      );
      if (bins.length > 0) setBinsToAsk(bins);

      if (summary.duplicates.length > 0) {
        setDuplicateFiles(summary.duplicates);
        setShowDuplicateDialog(true);
      }

      if (summary.failed.length > 0) {
        // Reported, not thrown: throwing aborted the batch in the uploader and
        // held every file selected — including the stored ones (#1172). The
        // names come back instead, so only real failures stay selected.
        showError(summary.failed[0], t('fileManagement.failedToUpload'));
        setError(summary.failed[0].message || t('fileManagement.failedToUpload'));
      }

      return { failedIndices: summary.failedIndices };
    } finally {
      setUploadLoading(false);
    }
  };

  const handleAnalyze = async (file: File, flightType?: string) => {
    setUploadLoading(true);
    setError('');
    setAnalysisResult(undefined);

    try {
      const response = await analyzeFileMutation.mutateAsync({ file, flightType });
      if (response.success) {
        setAnalysisResult(response);
        showSuccess(t('fileManagement.fileAnalyzed'));
      } else {
        showError(response.error || t('fileManagement.failedToAnalyze'));
        setError(t('fileManagement.failedToAnalyze'));
      }
    } catch (err) {
      showError(err, t('fileManagement.failedToAnalyze'));
      setError(t('fileManagement.failedToAnalyze'));
    } finally {
      setUploadLoading(false);
    }
  };

  // Takes the ENTRY. A registry-backed row is presigned by id, which is the only
  // addressing that reaches a file inside a folder somebody shared; an R-9 entry
  // with no row falls back to the path endpoint (see the mutation).
  const handleDownload = async (entry: FileItem) => {
    setError('');
    try {
      const response = await getDownloadUrlMutation.mutateAsync({
        objectId: entry.object_id,
        // `downloadPath`, never `key`: `key` is display and falls back to a
        // label for an R-9 entry, so sending it as a path 404s. The menu
        // withholds the action when neither address exists.
        fileKey: entry.downloadPath ?? '',
      });
      if (response.download_url) {
        window.open(response.download_url, '_blank');
        showSuccess(t('fileManagement.downloadStarted'));
      } else {
        showError(t('fileManagement.failedToGenerateUrl'), t('fileManagement.failedToDownload'));
        setError(t('fileManagement.failedToGenerateUrl'));
      }
    } catch (err) {
      showError(err, t('fileManagement.failedToDownload'));
      setError(t('fileManagement.failedToDownload'));
    }
  };

  // ONE delete handler for files and folders, because there is one endpoint for
  // both: `DELETE /api/file-objects/{id}` reads file-vs-folder off `blob_key IS
  // NULL`, the only place the schema records it (M-4). `recursive` is sent for a
  // folder and ignored for a file, preserving the browser's long-standing
  // "delete a folder takes its contents" behaviour.
  const handleDelete = async (entry: FileItem) => {
    setError('');
    const isFolder = !!entry.isFolder;
    const failureKey = isFolder
      ? 'fileManagement.failedToDeleteFolder'
      : 'fileManagement.failedToDelete';
    if (!entry.object_id) {
      // Unreachable from the menu (the item is hidden without a row, R-9) and
      // stated anyway: this is the action, and an entry with no id has nothing
      // to address. Silence here would look like a delete that worked.
      showError(t(failureKey), t(failureKey));
      setError(t(failureKey));
      return;
    }
    try {
      const response = await deleteObjectMutation.mutateAsync({
        objectId: entry.object_id,
        recursive: isFolder,
      });
      if (response.success) {
        showSuccess(t(isFolder ? 'fileManagement.folderDeleted' : 'fileManagement.fileDeleted'));
      } else {
        showError(t(failureKey), t(failureKey));
        setError(t(failureKey));
      }
    } catch (err) {
      showError(err, t(failureKey));
      setError(err instanceof Error ? err.message : t(failureKey));
    }
  };

  const handleMoveMany = async (items: FileItem[], target: FolderRef): Promise<string[]> => {
    setError('');
    // Total since B-4 — see `handleCreateFolder`.
    const parentId = parentIdFor(target);

    const results: MoveOutcome[] = [];
    for (const item of items) {
      // Entries with no registry row are reported as failures rather than sent
      // — R-13's share doctrine applied to move, for the same reason: there is
      // no row for `PATCH /api/file-objects/{id}` to name. They stay selected
      // with everything else that did not move.
      if (!item.object_id) {
        results.push(moveOutcome(item, false, t('fileManagement.failedToMove')));
        continue;
      }
      try {
        const response = await moveItemMutation.mutateAsync({
          objectId: item.object_id,
          parentId,
        });
        results.push(moveOutcome(item, !!response.object_id, undefined));
      } catch (err) {
        results.push(moveOutcome(item, false, err instanceof Error ? err.message : String(err)));
      }
    }

    const summary = summarizeMoveResults(results);
    if (summary.failed.length === 0) {
      showSuccess(t('fileManagement.itemsMoved', { count: summary.movedCount }));
    } else {
      // `key` is the display path; the last segment is the label. `MoveOutcome`
      // deliberately carries ids and keys only — it is a result set, not a row.
      const names = summary.failed.map((f) => f.key.split('/').pop() || f.key).join(', ');
      const msg = t('fileManagement.itemsMovedPartial', {
        moved: summary.movedCount,
        failed: summary.failed.length,
        names,
      });
      showError(msg, t('fileManagement.failedToMove'));
      setError(msg);
    }
    // Ids, not paths: the caller restores the retry-selection from these, and a
    // path cannot name one of two rows that share it (STD-029 R-2).
    return summary.failedIds;
  };

  const handleRenameFolder = async (entry: FileItem, newName: string) => {
    setError('');
    if (!entry.object_id) {
      showError(t('fileManagement.failedToRenameFolder'), t('fileManagement.failedToRenameFolder'));
      setError(t('fileManagement.failedToRenameFolder'));
      return;
    }
    try {
      await renameObjectMutation.mutateAsync({ objectId: entry.object_id, newName });
      // **No client-side re-point any more.** A rename is one row UPDATE: the id
      // is unchanged (M-7) and the path is re-derived by the server on the next
      // listing, which the invalidation above triggers. `refAfterRename` existed
      // to rewrite the path half of the ref, and the ref has no path half.
      showSuccess(t('fileManagement.folderRenamed'));
    } catch (err) {
      showError(err, t('fileManagement.failedToRenameFolder'));
      setError(err instanceof Error ? err.message : t('fileManagement.failedToRenameFolder'));
    }
  };

  const handleCreateFolder = async (parent: FolderRef, name: string) => {
    setError('');
    // Total since B-4: `null` means the caller's own root and every other
    // destination is a real node id. The "we can name it but not address it"
    // branch this used to carry stopped existing when the listing began carrying
    // the ancestor chain.
    const parentId = parentIdFor(parent);
    try {
      // No `success` flag to branch on: the id-addressed endpoints answer 201
      // with the created row or raise. A 200-with-`success:false` was the shape
      // that let #1244's silent no-op look like a working create for a full day.
      await createFolderMutation.mutateAsync({ parentId, name });
      showSuccess(t('fileManagement.folderCreated'));
    } catch (err) {
      showError(err, t('fileManagement.failedToCreateFolder'));
      setError(err instanceof Error ? err.message : t('fileManagement.failedToCreateFolder'));
    }
  };

  // A navigation, and since B-5 that means the url too — `setFilesFolder`
  // pushes a history entry, so Back returns to the folder you came from.
  //
  // Except when it is not a navigation (K1). Clicking the crumb for the folder
  // already on screen must cost nothing, and at the caller's own root the
  // store's `null` and the Home crumb's real row id name one node in two
  // spellings — an id comparison inside the store cannot see that, so it pushed
  // an entry, changed the query key and cost a refetch plus an extra Back press
  // for the rest of the session. The LISTING can see it, so the question is
  // asked here, at the click, rather than in the store's guard.
  const handleNavigate = (folder: FolderRef) => {
    if (isCurrentNode(listing, folder)) return;
    setFilesFolder(folder);
  };

  const handleCloseAnalysis = () => {
    setAnalysisResult(undefined);
  };

  const handleNavigateToDocuments = () => {
    setShowDocumentsView(true);
  };

  const handleBackToFiles = () => {
    setShowDocumentsView(false);
  };

  const handleNavigateToReports = () => {
    setShowReportsView(true);
  };

  const handleNavigateToShared = () => {
    setShowSharedView(true);
  };

  /**
   * Open a folder another organization shared, from the Shared view (D-6).
   *
   * The SAME browser renders it. R-12's "one Shared surface" is about where
   * grants are *listed*; decision 4 is explicit that grantees browse into shared
   * folders, and two browser components would be two places to fix the next bug.
   * What changes is the breadcrumb (no Home crumb — there is no path from
   * somebody else's subtree to your own root) and the upload zone (withheld).
   */
  const handleOpenSharedFolder = (objectId: string) => {
    setShowSharedView(false);
    // A real navigation since B-5, which is the whole of R-10's third
    // requirement: a folder another organization shared is now addressable,
    // reloadable and back-able exactly like one of your own, because the url
    // names the row id and nothing about it is relative to a tree.
    setFilesFolder({ id: objectId });
  };

  const handleBackFromShared = () => {
    setShowSharedView(false);
  };

  const handleBackFromReports = () => {
    setShowReportsView(false);
  };

  // A url that leads nowhere gets a panel, not a grid and not a toast (B-5).
  // Rendered ahead of the peer views because it is a statement about the
  // ADDRESS the browser was asked to open, and one action is enough: back to
  // `/files`, replacing the dead entry rather than pushing over it — a dead
  // address is not a place, so Back should return where the user really came
  // from.
  if (deadLink) {
    return (
      <FileManagementContainer>
        {onClose && (
          <CloseButton onClick={onClose}>
            <CloseIcon />
          </CloseButton>
        )}
        <FolderNotFound
          kind={deadLink}
          onBackToRoot={() => setFilesFolder(ROOT_FOLDER, { replace: true })}
        />
      </FileManagementContainer>
    );
  }

  // Show the "Shared with me" view if enabled
  if (showSharedView) {
    return (
      <FileManagementContainer>
        {onClose && (
          <CloseButton onClick={onClose}>
            <CloseIcon />
          </CloseButton>
        )}
        <SharedView
          onBack={handleBackFromShared}
          onOpenFolder={handleOpenSharedFolder}
          canAdministerShares={canAdministerShares}
        />
      </FileManagementContainer>
    );
  }

  // Show Reports view if enabled
  if (showReportsView) {
    return (
      <FileManagementContainer>
        {onClose && (
          <CloseButton onClick={onClose}>
            <CloseIcon />
          </CloseButton>
        )}
        <ReportsView onBack={handleBackFromReports} />
      </FileManagementContainer>
    );
  }

  // Show Documents view if enabled
  if (showDocumentsView) {
    return (
      <FileManagementContainer>
        {onClose && (
          <CloseButton onClick={onClose}>
            <CloseIcon />
          </CloseButton>
        )}
        <DocumentsView onBack={handleBackToFiles} />
      </FileManagementContainer>
    );
  }

  return (
    <FileManagementContainer>
      {onClose && (
        <CloseButton onClick={onClose}>
          <CloseIcon />
        </CloseButton>
      )}

      <Typography
        variant="h5"
        gutterBottom
        sx={{ color: BRAND_COLORS.forestGreen.main, fontWeight: 'bold' }}
      >
        {t('fileManagement.title')}
      </Typography>

      <FileBrowser
        files={files}
        loading={loading}
        error={error}
        currentFolder={currentFolder}
        crumbs={crumbs}
        insideShare={insideShare}
        // The open folder's display path — the upload `destination_path`, and
        // the only consumer of a path left in this component.
        currentPath={listing?.node?.path ?? ''}
        total={listing?.total ?? 0}
        pageCount={pageEntryCount(listing)}
        onBackToShared={handleNavigateToShared}
        onNavigate={handleNavigate}
        onDownload={handleDownload}
        onDelete={handleDelete}
        onMoveMany={handleMoveMany}
        onRenameFolder={handleRenameFolder}
        onCreateFolder={handleCreateFolder}
        onUpload={handleUpload}
        onAnalyze={handleAnalyze}
        uploadLoading={uploadLoading}
        analysisResult={analysisResult}
        onCloseAnalysis={handleCloseAnalysis}
        onNavigateToDocuments={handleNavigateToDocuments}
        onNavigateToReports={handleNavigateToReports}
        onNavigateToShared={handleNavigateToShared}
        onFlightUpdated={() => {
          void childrenQuery.refetch();
        }}
        fileFilters={fileFilters}
        serialNumbers={serialNumbers}
        onFileFilterChange={onFileFilterChange}
        onResetFileFilters={onResetFileFilters}
      />

      <BinUploadMatchPrompt uploads={binsToAsk} onDone={clearBinsToAsk} />

      <Dialog
        open={showDuplicateDialog}
        onClose={() => setShowDuplicateDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('fileManagement.duplicateFilesSkipped')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {t('fileManagement.duplicateFilesMessage', { count: duplicateFiles.length })}
          </Typography>
          <List dense>
            {duplicateFiles.map((dup, index) => (
              <ListItem key={index}>
                <ListItemText primary={dup.fileName} secondary={dup.message} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDuplicateDialog(false)} variant="contained">
            {t('common.ok')}
          </Button>
        </DialogActions>
      </Dialog>
    </FileManagementContainer>
  );
};

export default FileManagement;
